import { useEffect, useCallback, useState, useRef } from 'react';
import { useMeasurementWizardStore } from '@/store/useMeasurementWizardStore';
import { useDeviceStore } from '@/store/useDeviceStore';
import { useDeviceService } from '@/contexts/DeviceServiceContext';
import { testService } from '@/services/api/testService';
import { RECOVERY, isUnknownDeviceUid } from '@/services/device/constants';
import logger from '@/lib/logger';

export type RecoveryModalState =
  | 'stale'
  | 'auto-reconnecting'
  | 'probing'
  | 'harvesting'
  | 'recovered'
  | 'resuming'
  | 'lost'
  | 'reconnect-failed';

export interface InterruptedMeasurementRecoveryState {
  modalState: RecoveryModalState;
  error: string | null;
  isConnecting: boolean;
  interruptedAge: number | null;
  handleTryAgain: () => void;
  handleRetryProbe: () => void;
  handleResume: () => void;
  handleContinueResume: () => Promise<void>;
  handleAbortMeasurement: () => Promise<void>;
  handleDiscard: () => Promise<void>;
}

interface Options {
  phase: 'baseline' | 'specimen';
  onResume: (outcome: 'harvested' | 'still-running') => void;
  onDiscard: () => void;
}

export function useInterruptedMeasurementRecovery({
  phase,
  onResume,
  onDiscard,
}: Options): InterruptedMeasurementRecoveryState {
  const deviceService = useDeviceService();

  const measurementInFlight = useMeasurementWizardStore((s) => s.measurementInFlight);
  const testId = useMeasurementWizardStore((s) => s.testId);
  const clearMeasurementInFlight = useMeasurementWizardStore((s) => s.clearMeasurementInFlight);
  const setBaselineResult = useMeasurementWizardStore((s) => s.setBaselineResult);
  const setBaselineCompletedAt = useMeasurementWizardStore((s) => s.setBaselineCompletedAt);
  const setSpecimenResult = useMeasurementWizardStore((s) => s.setSpecimenResult);
  const setRecoveryInProgress = useMeasurementWizardStore((s) => s.setRecoveryInProgress);
  const resetWizard = useMeasurementWizardStore((s) => s.resetWizard);

  const interruptedAge = measurementInFlight
    ? Date.now() - measurementInFlight.startedAt
    : null;

  const isStaleAtMount = useRef(
    measurementInFlight
      ? Date.now() - measurementInFlight.startedAt > RECOVERY.STALENESS_THRESHOLD_MS
      : false,
  );

  const initialState: RecoveryModalState = isStaleAtMount.current
    ? 'stale'
    : 'auto-reconnecting';

  const [modalState, setModalState] = useState<RecoveryModalState>(initialState);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef(false);
  // Stable ref so handlers always call the latest callback without resetting mount effect
  const onResumeRef = useRef(onResume);
  onResumeRef.current = onResume;

  // Track modalState in a ref so the isConnected effect below can read it without
  // being listed as a dependency (avoids re-registering the effect on every state change).
  const modalStateRef = useRef(modalState);
  useEffect(() => {
    modalStateRef.current = modalState;
  }, [modalState]);

  // Subscribe to the device connection state.
  const isDeviceConnected = useDeviceStore((s) => !s.isEffectivelyDisconnected());

  const runProbe = useCallback(async () => {
    setModalState('probing');
    setError(null);

    // WORKAROUND: RESEND is non-functional at the protocol layer.
    // The device echoes the *original* message ID in its RESEND reply rather than
    // the RESEND command's own ID, so our ID-matched resolver discards the frame
    // as unrecognised and times out. Until the firmware is fixed to echo the
    // correct ID, we use the following two-step strategy instead:
    //   1. Query device state directly (getState / NONE trigger) to detect whether
    //      a measurement is still in progress.
    //   2. If the device is idle, check the backend: if the result was persisted
    //      before the interruption occurred, we can recover from there.

    let stateResponse: Awaited<ReturnType<typeof deviceService.getState>> = null;
    try {
      stateResponse = await deviceService.getState();
    } catch (err) {
      logger.warn('[RECOVERY:Modal] getState() probe failed:', err);
      if (!abortRef.current) {
        setModalState('reconnect-failed');
        setError('Device did not respond to recovery probe.');
      }
      return;
    }

    if (abortRef.current) return;

    const processingId = stateResponse?.state?.processingId ?? 0;

    // Device still measuring — subscribe to the in-progress measurement.
    if (processingId > 0) {
      setModalState('resuming');
      return;
    }

    // Device is idle. Check whether the result was already saved to the backend
    // before the interruption occurred.
    if (!testId) {
      setModalState('lost');
      setError(
        'The reader has no result for this measurement. It may have been restarted or the measurement window passed.',
      );
      return;
    }

    try {
      const testDetail = await testService.getTestById(testId);

      if (abortRef.current) return;

      // Determine whether the relevant measurement phase was persisted.
      const phaseSaved =
        phase === 'baseline'
          ? !!testDetail.baselineCompletedAt
          : !!(testDetail.specimenCompletedAt ?? testDetail.completedAt);

      if (phaseSaved) {
        // Result is on the backend — build a minimal placeholder for the wizard
        // store. The actual impedance data will be fetched by ResultsView from
        // the backend; we only need a non-null value to satisfy the wizard guard.
        const deviceInfo = useDeviceStore.getState().deviceInfo;
        const nowDate = new Date();
        const fakeResult = {
          deviceUid: deviceInfo?.uid ?? '',
          deviceSerialNumber: deviceInfo?.serialNumber ?? 0,
          firmwareVersion: deviceInfo?.firmwareVersion ?? '',
          hardwareVersion: deviceInfo?.hardwareVersion ?? '',
          durationMs: 0,
          impedanceResult: {} as any,
          completedAt: nowDate,
        };

        if (phase === 'baseline') {
          setBaselineResult(fakeResult as any);
          setBaselineCompletedAt(
            testDetail.baselineCompletedAt
              ? new Date(testDetail.baselineCompletedAt)
              : nowDate,
          );
        } else {
          setSpecimenResult(fakeResult as any);
        }

        setModalState('recovered');
        return;
      }

      // Backend has no record of the result — measurement data is lost.
      setModalState('lost');
      setError(
        'The reader has no result for this measurement. It may have been restarted or the measurement window passed.',
      );
    } catch (err) {
      logger.warn('[RECOVERY:Modal] Backend result check failed:', err);
      if (!abortRef.current) {
        setModalState('reconnect-failed');
        setError('Could not verify measurement status with the server. Please check your connection and try again.');
      }
    }
  }, [
    deviceService,
    phase,
    testId,
    setBaselineResult,
    setBaselineCompletedAt,
    setSpecimenResult,
  ]);

  const runReconnect = useCallback(async () => {
    logger.warn('[RECOVERY:Modal] runReconnect() called');
    setError(null);
    setRecoveryInProgress(true);

    const reconnected = await deviceService.tryAutoReconnect();

    if (abortRef.current) return;

    if (!reconnected) {
      setModalState('reconnect-failed');
      setError(
        'Could not reconnect to the reader. Make sure it is powered on and connected, then try again.',
      );
      setRecoveryInProgress(false);
      return;
    }

    const connectedUid = useDeviceStore.getState().deviceInfo?.uid;
    if (
      !isUnknownDeviceUid(connectedUid) &&
      !isUnknownDeviceUid(measurementInFlight?.deviceUid) &&
      connectedUid !== measurementInFlight!.deviceUid
    ) {
      setModalState('reconnect-failed');
      setError('Connected reader does not match the one used for the interrupted measurement.');
      setRecoveryInProgress(false);
      return;
    }

    try {
      await runProbe();
    } catch (err) {
      logger.warn('[RECOVERY:Modal] runProbe threw unexpectedly:', err);
      setModalState('reconnect-failed');
      setError('An unexpected error occurred during recovery. Please try again.');
    }
    setRecoveryInProgress(false);
  }, [deviceService, measurementInFlight, setRecoveryInProgress, runProbe]);

  // Auto-reconnect on mount. Both refresh and in-session disconnect triggers now follow the
  // same path: attempt reconnect silently, then probe the device.
  useEffect(() => {
    abortRef.current = false; // reset for this invocation (handles StrictMode double-invoke)
    logger.warn(
      '[RECOVERY:Modal] Mounted — phase:',
      phase,
      '| isStale:',
      isStaleAtMount.current,
    );
    if (!isStaleAtMount.current) {
      runReconnect();
    }
    return () => {
      abortRef.current = true;
      isStaleAtMount.current = true; // prevent StrictMode second invoke from starting a redundant reconnect
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When DeviceService.handleConnect reconnects the device externally (USB plug-in event)
  // while the modal is stuck in 'reconnect-failed' or still in 'auto-reconnecting', skip
  // tryAutoReconnect() and jump straight to probing. This aligns specimen-measurement
  // behavior with baseline: baseline's modal unmounts automatically when isConnected
  // becomes true (its render condition is !isConnected), while the specimen modal is
  // controlled by explicit showDisconnectRecovery state and therefore persists — so
  // without this effect the modal stays frozen at "Could Not Reconnect" after plug-in.
  useEffect(() => {
    if (!isDeviceConnected || abortRef.current) return;
    const state = modalStateRef.current;
    if (state !== 'reconnect-failed' && state !== 'auto-reconnecting') return;

    // Device reconnected externally — check UID match before probing.
    const connectedUid = useDeviceStore.getState().deviceInfo?.uid;
    const inFlight = useMeasurementWizardStore.getState().measurementInFlight;
    if (
      !isUnknownDeviceUid(connectedUid) &&
      !isUnknownDeviceUid(inFlight?.deviceUid) &&
      connectedUid !== inFlight?.deviceUid
    ) {
      setModalState('reconnect-failed');
      setError('Connected reader does not match the one used for the interrupted measurement.');
      return;
    }

    logger.debug('[RECOVERY:Modal] Device reconnected externally — auto-advancing to probe');
    runProbe();
  // runProbe is stable; modalStateRef/abortRef/isDeviceConnected are the only reactive deps needed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeviceConnected, runProbe]);

  const handleTryAgain = useCallback(() => {
    setModalState('auto-reconnecting');
    runReconnect();
  }, [runReconnect]);

  const handleRetryProbe = useCallback(() => {
    setModalState('probing');
    runProbe();
  }, [runProbe]);

  const handleResume = useCallback(() => {
    clearMeasurementInFlight();
    onResume('harvested');
  }, [clearMeasurementInFlight, onResume]);

  const handleContinueResume = useCallback(async () => {
    if (abortRef.current) return;
    try {
      const stateResp = await deviceService.getState();
      if (abortRef.current) return;
      if ((stateResp?.state?.processingId ?? 0) > 0) {
        onResumeRef.current('still-running');
      } else {
        // Measurement finished while dialog was open — re-probe to harvest result
        await runProbe();
      }
    } catch {
      // Can't query device — fall back to subscribe path
      if (!abortRef.current) onResumeRef.current('still-running');
    }
  }, [deviceService, runProbe]);

  const handleAbortMeasurement = useCallback(async () => {
    abortRef.current = true;
    await deviceService.cancelMeasurement();
    if (testId) {
      await testService.cancelTest(testId).catch((err) => {
        logger.warn('[RECOVERY:Modal] Failed to cancel test on backend:', err);
      });
    }
    clearMeasurementInFlight();
    resetWizard();
    onDiscard();
  }, [deviceService, testId, clearMeasurementInFlight, resetWizard, onDiscard]);

  const handleDiscard = useCallback(async () => {
    abortRef.current = true;
    // Best-effort ABORT — silently ignored when device is disconnected or idle.
    await deviceService.cancelMeasurement().catch((err) => {
      logger.warn('[RECOVERY:Modal] cancelMeasurement during discard failed (non-fatal):', err);
    });
    if (testId) {
      await testService.cancelTest(testId).catch((err) => {
        logger.warn('[RECOVERY:Modal] Failed to cancel test on backend:', err);
      });
    }
    clearMeasurementInFlight();
    resetWizard();
    onDiscard();
  }, [deviceService, testId, clearMeasurementInFlight, resetWizard, onDiscard]);

  const isConnecting =
    modalState === 'auto-reconnecting' ||
    modalState === 'probing' ||
    modalState === 'harvesting';

  return {
    modalState,
    error,
    isConnecting,
    interruptedAge,
    handleTryAgain,
    handleRetryProbe,
    handleResume,
    handleContinueResume,
    handleAbortMeasurement,
    handleDiscard,
  };
}
